import React from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  Straighten as MeasureIcon,
  Draw as DrawIcon,
  Bookmark as BookmarkIcon,
  Layers as LegendIcon,
  NearMe as SelectIcon,
} from '@mui/icons-material';

export type MapTool = 'select' | 'measure' | 'draw' | null;

export interface MapToolbarProps {
  activeTool: MapTool;
  onToolChange: (tool: MapTool) => void;
  showLegend: boolean;
  onToggleLegend: () => void;
  showBookmarks: boolean;
  onToggleBookmarks: () => void;
  /** Hide draw tool (e.g. in view-only mode) */
  hideDraw?: boolean;
}

const MapToolbar: React.FC<MapToolbarProps> = ({
  activeTool,
  onToolChange,
  showLegend,
  onToggleLegend,
  showBookmarks,
  onToggleBookmarks,
  hideDraw,
}) => {
  const toggle = (tool: MapTool) => onToolChange(activeTool === tool ? null : tool);

  const btn = (
    tool: MapTool,
    icon: React.ReactNode,
    label: string,
  ) => (
    <Tooltip title={label} placement="right">
      <IconButton
        size="small"
        onClick={() => toggle(tool)}
        sx={{
          p: 0.5,
          color: activeTool === tool ? 'primary.main' : 'text.secondary',
          bgcolor: activeTool === tool ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        bgcolor: 'rgba(30,30,30,0.92)',
        borderRadius: 0.75,
        p: 0.25,
        backdropFilter: 'blur(6px)',
        boxShadow: 2,
      }}
    >
      {btn('select', <SelectIcon sx={{ fontSize: 16 }} />, 'Select features')}
      {btn('measure', <MeasureIcon sx={{ fontSize: 16 }} />, 'Measure distance')}
      {!hideDraw && btn('draw', <DrawIcon sx={{ fontSize: 16 }} />, 'Draw / annotate')}
      <Divider sx={{ my: 0.25, borderColor: 'rgba(255,255,255,0.12)' }} />
      <Tooltip title="Legend" placement="right">
        <IconButton
          size="small"
          onClick={onToggleLegend}
          sx={{
            p: 0.5,
            color: showLegend ? 'primary.main' : 'text.secondary',
            bgcolor: showLegend ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <LegendIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Bookmarks" placement="right">
        <IconButton
          size="small"
          onClick={onToggleBookmarks}
          sx={{
            p: 0.5,
            color: showBookmarks ? 'primary.main' : 'text.secondary',
            bgcolor: showBookmarks ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <BookmarkIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MapToolbar;
